import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { getAssetHierarchyPalette, getContainedButtonSx, getDialogSectionBoxSx, getOutlinedButtonSx } from "../assetHierarchyStyles";
import { getValidReparentTargets, toSelectOptions } from "../assetHierarchyUtils";
import cmmsMessages from "../cmmsMessages";
import messages from "../messages";

export default function ReparentDialog({ nodes, onClose, onMove, open, selectedNode }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const validTargets = useMemo(() => getValidReparentTargets(nodes, selectedNode), [nodes, selectedNode]);
  const options = useMemo(() => toSelectOptions(validTargets), [validTargets]);
  const [newParentAssetNodeId, setNewParentAssetNodeId] = useState("");

  useEffect(() => {
    if (open) {
      setNewParentAssetNodeId(options[0]?.id ?? "");
    }
  }, [open, options]);

  const handleChange = (event) => {
    setNewParentAssetNodeId(event.target.value);
  };

  const handleMove = () => {
    onMove(Number(newParentAssetNodeId));
  };

  return (
    <Popup openPopup={open} setOpenPopup={onClose} maxWidth="sm" title={intl.formatMessage(messages.moveDialogTitle)}>
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box sx={getDialogSectionBoxSx(cmmsColors)}>
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
            <FormattedMessage
              {...messages.moveDialogIntro}
              values={{
                name: (
                  <Box component="span" sx={{ color: cmmsColors.text, fontWeight: 700 }}>
                    {selectedNode?.name ?? intl.formatMessage(messages.moveDialogThisNode)}
                  </Box>
                ),
              }}
            />
          </Typography>
        </Box>
        <AxionControls.AxionSelect
          name="newParentAssetNodeId"
          label={intl.formatMessage(messages.moveDialogNewParent)}
          value={newParentAssetNodeId}
          onChange={handleChange}
          options={options}
          showNoneOption={false}
          disabled={!options.length}
        />
        {!options.length &&
          <Typography variant="body2" sx={{ color: cmmsColors.dangerText, fontSize: 14, mt: 2 }}>
            <FormattedMessage {...messages.moveDialogNoTargets} />
          </Typography>
        }
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={onClose} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleMove} disabled={!newParentAssetNodeId} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...messages.move} />
          </Button>
        </Stack>
      </Box>
    </Popup>
  );
}
